import { BaseParser, IValidateParams } from './BaseParser';
import {
  IParseResult,
  PreParseResult,
  ParseResult,
  TEDParseDateTimeMode,
  PreParseDateTimeOrigin,
  PreParseDateTimeValueType,
} from '../FormatterTypes';
import {
  formatterStrParamDef,
  formatterBoolParamDef,
  formatterIntParamDef,
} from '../Utils/ParamUtils';
import { nextFormatItem } from '../Utils/Other';
import { isLetter } from '../Utils/StringUtils';
import {
  weekNoToDate,
  firstDayInYear,
  getWeekDayInWeek,
  firstDayInMonth,
  getDayOfMonth,
  weeksInYear,
  firstDayInWeek,
  setHour,
  setMin,
  trunkToHour,
  trunkToMin,
  incYear,
  incMonth,
  incDay,
  incHour,
  incMin,
  getLastMonday,
} from '../Utils/DateTimeParserUtils';
import {
  getMonthText,
  getWeekDayText,
  getQuarterMonth,
  encodeDate,
  encodeTime,
} from '../Utils/TimeDateUtils';
import moment from 'moment';
import { IInternalDateFormat } from '../Displayers/TEDCustomDateTimeDisplayer';

interface IConvertFormatItem {
  pattern: string;
  index: number;
}

const numbers = '0123456789';
const preParseModes = [
  TEDParseDateTimeMode.pdmYMD,
  TEDParseDateTimeMode.pdmYM,
  TEDParseDateTimeMode.pdmY,
  TEDParseDateTimeMode.pdmHMS,
  TEDParseDateTimeMode.pdmHM,
  TEDParseDateTimeMode.pdmYMDHM,
  TEDParseDateTimeMode.pdmYW,
  TEDParseDateTimeMode.pdmDateString,
];
const weekdays = [
  PreParseDateTimeOrigin.ppdoMa,
  PreParseDateTimeOrigin.ppdoTi,
  PreParseDateTimeOrigin.ppdoOn,
  PreParseDateTimeOrigin.ppdoTo,
  PreParseDateTimeOrigin.ppdoFr,
  PreParseDateTimeOrigin.ppdoLo,
  PreParseDateTimeOrigin.ppdoSo,
];

interface IPreParseTimeValidate {
  valid: PreParseResult;
  origin: PreParseDateTimeOrigin;
  oper: string | null;
  value: number;
  valueType: PreParseDateTimeValueType;
}

export abstract class TEDCustomDateTimeParser extends BaseParser {
  abstract mode(): TEDParseDateTimeMode;

  defaultParams(): string {
    let result = '';

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'ValidDateTimeSeps=" Tt,.";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYW,
        TEDParseDateTimeMode.pdmY,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'CenturyLimit="+20";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYW,
        TEDParseDateTimeMode.pdmY,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'AssumeCentury="N";RequireCentury="N";';
    }

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmGeneral:
      case TEDParseDateTimeMode.pdmYMDHM:
      case TEDParseDateTimeMode.pdmDateTimeString:
        result += 'ValidDateSeps="-";';
        break;
      case TEDParseDateTimeMode.pdmYMD:
      case TEDParseDateTimeMode.pdmYM:
      case TEDParseDateTimeMode.pdmDateString:
        result += 'ValidDateSeps=" -/,.";';
        break;
      case TEDParseDateTimeMode.pdmYW:
        result += 'ValidDateSeps=" vV-/,.";';
        break;
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'AllowIncomplete="N";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'AllowZero="N";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYM,
        TEDParseDateTimeMode.pdmYMDHM,
      ].includes(this.mode())
    ) {
      result += 'DefMonth="";';
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmYMD,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateString,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'DefDay="";';
    }

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmGeneral:
      case TEDParseDateTimeMode.pdmYMDHM:
      case TEDParseDateTimeMode.pdmDateTimeString:
        result += 'ValidTimeSeps=":";';
        break;
      case TEDParseDateTimeMode.pdmHMS:
      case TEDParseDateTimeMode.pdmHM:
        result += 'ValidTimeSeps=" :,.";';
        break;
    }

    if (
      [
        TEDParseDateTimeMode.pdmGeneral,
        TEDParseDateTimeMode.pdmHMS,
        TEDParseDateTimeMode.pdmHM,
        TEDParseDateTimeMode.pdmH,
        TEDParseDateTimeMode.pdmYMDHM,
        TEDParseDateTimeMode.pdmDateTimeString,
      ].includes(this.mode())
    ) {
      result += 'UseTime24="N";';
    }

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmDateString:
      case TEDParseDateTimeMode.pdmDateTimeString:
        result += 'Format="";MonthTexts="";DayTexts="";';
        break;
      case TEDParseDateTimeMode.pdmGeneral:
        result +=
          'Format="y-m-d/ymmdd";MonthTexts="";DayTexts="";AmStr="fm";PmStr="em";';
        break;
    }

    return result.replace(/;+$/, '');
  }

  getValidateParams(params: string): IValidateParams {
    const validDateTimeSeps = formatterStrParamDef(
      'ValidDateTimeSeps',
      params,
      this.defaultParams()
    );
    const validDateSeps = formatterStrParamDef(
      'ValidDateSeps',
      params,
      this.defaultParams()
    );
    const validTimeSeps = formatterStrParamDef(
      'ValidTimeSeps',
      params,
      this.defaultParams()
    );

    return {
      inputsList: numbers,
      separators: validDateTimeSeps.concat(validDateSeps).concat(validTimeSeps),
    };
  }

  parse(text: string, params: string = ''): IParseResult {
    const { pattern, listOrder } = this.makeRegEx(params);
    if (pattern) {
      const match = text.match(pattern);
      if (match) {
        const partsAll = match.flat().slice(1);
        const partsFiltered: string[] = [];
        const listOrderFiltered: string[] = [];
        for (let i = 0; i < partsAll.length; i++) {
          const part = partsAll[i];
          if (part) {
            partsFiltered.push(part);
            listOrderFiltered.push(listOrder[i]);
          }
        }
        const value = this.interpretParts(
          partsFiltered,
          listOrderFiltered,
          params
        );

        return {
          result: value.result,
          value: value.value ?? text,
        };
      }
    }
    return {
      result: text.length === 0 ? ParseResult.OK : ParseResult.ERROR,
      value: text,
    };
  }

  preParse(text: string, params: string = ''): IParseResult {
    let value = new Date();
    const mode = this.mode();
    let {
      valid: result,
      oper,
      origin,
      value: opValue,
      valueType,
    } = this.preParseDateTimeValidate(text);
    const date = new Date();
    if (result) {
      switch (origin) {
        case PreParseDateTimeOrigin.ppdoAr:
          if (oper === '=') {
            if (opValue >= 1000 && opValue <= 9999) {
              if (mode === TEDParseDateTimeMode.pdmYW) {
                value = weekNoToDate(opValue, 1);
              } else {
                value = new Date(opValue, 1, 1);
              }
            } else {
              result = PreParseResult.UNDEFINED;
            }
          } else {
            if (mode === TEDParseDateTimeMode.pdmYW) {
              value = weekNoToDate(new Date().getFullYear(), 1);
            } else {
              value = firstDayInYear();
            }
          }
          break;
        case PreParseDateTimeOrigin.ppdoMan:
          if (oper === '=') {
            if (opValue >= 1 && opValue <= 12) {
              if (mode === TEDParseDateTimeMode.pdmYW) {
                value = getWeekDayInWeek(new Date().getFullYear(), opValue, 1);
              } else {
                value = new Date(new Date().getFullYear(), opValue, 1);
              }
            } else {
              result = PreParseResult.UNDEFINED;
            }
          } else {
            if (mode === TEDParseDateTimeMode.pdmYW) {
              const date = new Date();
              value = getWeekDayInWeek(date.getFullYear(), date.getMonth(), 1); //GetWeekDayInWeek
            } else {
              value = firstDayInMonth();
            }
          }
          break;
        case PreParseDateTimeOrigin.ppdoDatum:
          if (oper === '=') {
            if (opValue >= 1 && opValue <= 31) {
              value = getDayOfMonth(opValue);
            } else {
              result = PreParseResult.UNDEFINED;
            }
          } else {
            value = new Date();
          }
          break;
        case PreParseDateTimeOrigin.ppdoV:
          if (oper === '=') {
            if (opValue >= 1 && opValue <= weeksInYear()) {
              value = weekNoToDate(new Date().getFullYear(), opValue);
            } else {
              result = PreParseResult.UNDEFINED;
            }
          } else {
            value = firstDayInWeek();
          }
          break;
        case PreParseDateTimeOrigin.ppdoTim:
          value = trunkToHour();
          if (oper === '=') {
            if (opValue >= 1 && opValue <= 23) {
              value = setHour(value, opValue);
            } else {
              result = PreParseResult.UNDEFINED;
            }
          }
          break;
        case PreParseDateTimeOrigin.ppdoMin:
          value = trunkToMin();
          if (oper === '=') {
            if (opValue >= 1 && opValue <= 59) {
              setMin(value, opValue);
            } else {
              result = PreParseResult.UNDEFINED;
            }
          }
          break;
        case PreParseDateTimeOrigin.ppdoMa:
        case PreParseDateTimeOrigin.ppdoTi:
        case PreParseDateTimeOrigin.ppdoOn:
        case PreParseDateTimeOrigin.ppdoTo:
        case PreParseDateTimeOrigin.ppdoFr:
        case PreParseDateTimeOrigin.ppdoLo:
        case PreParseDateTimeOrigin.ppdoSo:
          getWeekDayInWeek(date.getFullYear(), date.getMonth(), origin - 6);
          break;
      }
      if (oper === '+' || oper === '-') {
        if (valueType === PreParseDateTimeValueType.ppdvNone) {
          switch (origin) {
            case PreParseDateTimeOrigin.ppdoAr:
              valueType = PreParseDateTimeValueType.ppdvAr;
              break;
            case PreParseDateTimeOrigin.ppdoMan:
              valueType = PreParseDateTimeValueType.ppdvMan;
              break;
            case PreParseDateTimeOrigin.ppdoV:
              valueType = PreParseDateTimeValueType.ppdvV;
              break;
            case PreParseDateTimeOrigin.ppdoTim:
              valueType = PreParseDateTimeValueType.ppdvTim;
              break;
            case PreParseDateTimeOrigin.ppdoMin:
              valueType = PreParseDateTimeValueType.ppdvMin;
              break;
            default:
              valueType = PreParseDateTimeValueType.ppdvDag;
              break;
          }
        }
        if (oper === '-') {
          opValue = -opValue;
        }
        switch (valueType) {
          case PreParseDateTimeValueType.ppdvAr:
            value = incYear(value, opValue);
            break;
          case PreParseDateTimeValueType.ppdvMan:
            value = incMonth(value, opValue);
            break;
          case PreParseDateTimeValueType.ppdvDag:
            value = incDay(value, opValue);
            break;
          case PreParseDateTimeValueType.ppdvV:
            value = incDay(value, opValue * 7);
            break;
          case PreParseDateTimeValueType.ppdvTim:
            value = incHour(value, opValue);
            break;
          case PreParseDateTimeValueType.ppdvMin:
            value = incMin(value, opValue);
            break;
        }
      }
      switch (mode) {
        case TEDParseDateTimeMode.pdmYW:
          value = getLastMonday(value); // Inte måndag? Backa till måndag
          break;
      }
    }
    return { value: value.toLocaleString('sv-SE'), result };
  }

  preParseDateTimeValidate(text: string): IPreParseTimeValidate {
    const mode = this.mode();
    const result: IPreParseTimeValidate = {
      valid: PreParseResult.UNDEFINED,
      origin: PreParseDateTimeOrigin.ppdoNone,
      oper: null,
      value: 0,
      valueType: PreParseDateTimeValueType.ppdvNone,
    };
    let workingString = text;

    if (
      preParseModes.includes(this.mode()) &&
      Array.from(text).every(x => isLetter(x))
    ) {
      let textString = workingString;
      const textCutoffIndex = /[0123456789+-=]/.exec(workingString)?.index;
      if (textCutoffIndex) {
        textString = workingString.slice(0, textCutoffIndex);
        workingString = workingString.slice(textCutoffIndex);
      }
      switch (textString.toLowerCase()) {
        case 'å':
        case 'år':
          result.origin = PreParseDateTimeOrigin.ppdoAr;
          break;
        case 'm':
          if (
            mode === TEDParseDateTimeMode.pdmHMS ||
            mode === TEDParseDateTimeMode.pdmHM ||
            mode === TEDParseDateTimeMode.pdmH
          ) {
            result.origin = PreParseDateTimeOrigin.ppdoMin;
          } else {
            result.origin = PreParseDateTimeOrigin.ppdoMan;
          }
          break;
        case 'mån':
        case 'månad':
        case 'månader':
          result.origin = PreParseDateTimeOrigin.ppdoMan;
          break;
        case 'datum':
        case 'd':
        case 'dag':
        case 'dagar':
          result.origin = PreParseDateTimeOrigin.ppdoDatum;
          break;
        case 'v':
        case 'v.':
        case 'vecka':
        case 'veckor':
          result.origin = PreParseDateTimeOrigin.ppdoV;
          break;
        case 't':
        case 'tim':
        case 'timme':
        case 'timmar':
          result.origin = PreParseDateTimeOrigin.ppdoTim;
          break;
        case 'min':
        case 'minut':
        case 'minuter':
          result.origin = PreParseDateTimeOrigin.ppdoMin;
          break;
        case 'må':
        case 'måndag':
          result.origin = PreParseDateTimeOrigin.ppdoMa;
          break;
        case 'ti':
        case 'tisdag':
          result.origin = PreParseDateTimeOrigin.ppdoTi;
          break;
        case 'on':
        case 'onsdag':
          result.origin = PreParseDateTimeOrigin.ppdoOn;
          break;
        case 'to':
        case 'torsdag':
          result.origin = PreParseDateTimeOrigin.ppdoTo;
          break;
        case 'fr':
        case 'fredag':
          result.origin = PreParseDateTimeOrigin.ppdoFr;
          break;
        case 'lö':
        case 'lördag':
          result.origin = PreParseDateTimeOrigin.ppdoLo;
          break;
        case 'sö':
        case 'söndag':
          result.origin = PreParseDateTimeOrigin.ppdoSo;
          break;
        default:
          result.valid = PreParseResult.UNDEFINED;
      }
      switch (result.origin) {
        case PreParseDateTimeOrigin.ppdoAr:
          if (
            ![
              TEDParseDateTimeMode.pdmYMD,
              TEDParseDateTimeMode.pdmYM,
              TEDParseDateTimeMode.pdmY,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmYW,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
        case PreParseDateTimeOrigin.ppdoMan:
          if (
            ![
              TEDParseDateTimeMode.pdmYMD,
              TEDParseDateTimeMode.pdmYM,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmYW,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
        case PreParseDateTimeOrigin.ppdoDatum:
        case PreParseDateTimeOrigin.ppdoMa:
        case PreParseDateTimeOrigin.ppdoTi:
        case PreParseDateTimeOrigin.ppdoOn:
        case PreParseDateTimeOrigin.ppdoTo:
        case PreParseDateTimeOrigin.ppdoFr:
        case PreParseDateTimeOrigin.ppdoLo:
        case PreParseDateTimeOrigin.ppdoSo:
          if (
            ![
              TEDParseDateTimeMode.pdmYMD,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
        case PreParseDateTimeOrigin.ppdoV:
          if (
            ![
              TEDParseDateTimeMode.pdmYMD,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmYW,
              TEDParseDateTimeMode.pdmYW,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
        case PreParseDateTimeOrigin.ppdoTim:
          if (
            ![
              TEDParseDateTimeMode.pdmHMS,
              TEDParseDateTimeMode.pdmHM,
              TEDParseDateTimeMode.pdmH,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
        case PreParseDateTimeOrigin.ppdoMin:
          if (
            ![
              TEDParseDateTimeMode.pdmHMS,
              TEDParseDateTimeMode.pdmHM,
              TEDParseDateTimeMode.pdmYMDHM,
              TEDParseDateTimeMode.pdmDateString,
            ].includes(mode)
          ) {
            result.valid = PreParseResult.UNDEFINED;
          }
          break;
      }

      //Oper
      if (['+', '-', '='].includes(workingString[0])) {
        result.oper = workingString[0];
        workingString = workingString.slice(1);
      }
      //Value
      let valueString = workingString;
      const valueCutoffIndex = /[a-ÿA-Ÿ]/.exec(workingString)?.index;
      if (valueCutoffIndex) {
        valueString = workingString.slice(0, valueCutoffIndex);
        workingString = workingString.slice(valueCutoffIndex);
      }

      if (valueString !== '') {
        const value = Number.parseInt(valueString);
        if (value) {
          result.value = value;
          result.oper = result.oper ?? '=';
        } else {
          result.valid = PreParseResult.UNDEFINED;
        }
      } else if (!result.oper) {
        result.valid = PreParseResult.UNDEFINED;
      }

      if (result.value !== 0 && !result.oper) {
        result.valid = PreParseResult.UNDEFINED;
      }

      if (
        (result.value !== 0 || result.oper === '=') &&
        weekdays.includes(result.origin)
      ) {
        result.valid = PreParseResult.UNDEFINED;
      }
      //ValueType
      let valueTypeString = workingString;
      if (
        valueTypeString &&
        (result.oper === '+' || result.oper === '-') &&
        result.value === 0
      ) {
        result.oper = null;
        valueTypeString = '';
      }
      switch (valueTypeString.toLowerCase()) {
        case '':
          result.valueType = PreParseDateTimeValueType.ppdvNone;
          break;
        case 'å':
        case 'år':
          result.valueType = PreParseDateTimeValueType.ppdvAr;
          break;
        case 'm':
          if (
            mode === TEDParseDateTimeMode.pdmHMS ||
            mode === TEDParseDateTimeMode.pdmHM ||
            mode === TEDParseDateTimeMode.pdmH
          ) {
            result.valueType = PreParseDateTimeValueType.ppdvMin;
          } else {
            result.valueType = PreParseDateTimeValueType.ppdvMan;
          }
          break;
        case 'mån':
        case 'månad':
        case 'månader':
          result.valueType = PreParseDateTimeValueType.ppdvMan;
          break;
        case 'd':
        case 'dag':
        case 'dagar':
          result.valueType = PreParseDateTimeValueType.ppdvDag;
          break;
        case 'v':
        case 'v.':
        case 'vecka':
        case 'veckor':
          result.valueType = PreParseDateTimeValueType.ppdvV;
          break;
        case 't':
        case 'tim':
        case 'timme':
        case 'timmar':
          result.valueType = PreParseDateTimeValueType.ppdvTim;
          break;
        case 'min':
        case 'minut':
        case 'minuter':
          result.valueType = PreParseDateTimeValueType.ppdvMin;
          break;
        default:
          result.valid = PreParseResult.UNDEFINED;
      }
    } else {
      result.valid = PreParseResult.UNDEFINED;
    }

    return result;
  }

  formatString(params: string = ''): string {
    let result = '';
    const defaultParams = this.defaultParams();
    const allowIncomplete = formatterBoolParamDef(
      'AllowIncomplete',
      params,
      defaultParams
    );
    const assumeCentury = formatterBoolParamDef(
      'AssumeCentury',
      params,
      defaultParams
    );
    const requireCentury = formatterBoolParamDef(
      'RequireCentury',
      params,
      defaultParams
    );
    const format = formatterStrParamDef('Format', params, defaultParams);

    switch (this.mode()) {
      case TEDParseDateTimeMode.pdmYMD:
        if (allowIncomplete) {
          if (requireCentury) {
            result = 'yyyy-m|-d/yyyy|mm|dd';
          } else if (assumeCentury) {
            result = 'y-m|-d/yyyy|mm|dd/yy';
          } else {
            result = 'y-m|-d/y/y|mm|dd';
          }
        } else {
          if (requireCentury) {
            result = 'yyyy-m-d/yyyymmdd';
          } else {
            result = 'y-m-d/ymmdd';
          }
        }
        break;
      case TEDParseDateTimeMode.pdmYM:
        if (allowIncomplete) {
          if (requireCentury) {
            result = 'yyyy-m/yyyy|mm';
          } else if (assumeCentury) {
            result = 'y|-m/yyyy|mm/yy';
          } else {
            result = 'y-m/y|mm';
          }
        } else {
          if (requireCentury) {
            result = 'yyyy-m/yyyymm';
          } else {
            result = 'y-m/ymm';
          }
        }
        break;
      case TEDParseDateTimeMode.pdmY:
        result = 'y';
        break;
      case TEDParseDateTimeMode.pdmHMS:
        result = 'h:n|:s/h|nn|ss';
        break;
      case TEDParseDateTimeMode.pdmHM:
        result = 'h:n/h|nn';
        break;
      case TEDParseDateTimeMode.pdmH:
        result = 'h';
        break;
      case TEDParseDateTimeMode.pdmDateTimeString:
      case TEDParseDateTimeMode.pdmYMDHM:
        if (allowIncomplete) {
          if (requireCentury) {
            result = 'yyyymmddhhnn/(yyyy-m|-d/yyyy|mm|dd)|.(h:n/h|nn)';
          } else if (assumeCentury) {
            result =
              'yyyymmddhhnn/yymmddhhnn/(y-m|-d/yyyy|mm|dd/yy)|.(h:n/h|nn)';
          } else {
            result = 'yyyymmddhhnn/yymmddhhnn/(y-m|-d/y|mm|dd)|.(h:n/h|nn)';
          }
        } else {
          if (requireCentury) {
            result = 'yyyymmddhhnn/(yyyy-m-d/yyyymmdd)|.(h:n/h|nn)';
          } else {
            result = 'yyyymmddhhnn/yymmddhhnn/(y-m-d/ymmdd)|.(h:n/h|nn)';
          }
        }
        break;
      case TEDParseDateTimeMode.pdmYW:
        result = 'y_-w/y_w';
        break;
      case TEDParseDateTimeMode.pdmDateString:
        if (!format) {
          if (allowIncomplete) {
            if (requireCentury) {
              result = 'yyyy-m|-d/yyyy|mm|dd';
            } else if (assumeCentury) {
              result = 'y-m|-d/yyyy|mm|dd/yy';
            } else {
              result = 'y-m|-d/y/y|mm|dd';
            }
          } else {
            if (requireCentury) {
              result = 'yyyy-m-d/yyyymmdd';
            } else {
              result = 'y-m-d/ymmdd';
            }
          }
        }
        break;
      default:
        result = format;
        break;
    }

    return result;
  }

  makeRegEx(params: string = ''): {
    pattern: RegExp | null;
    listOrder: string[];
  } {
    let pattern = '',
      validSeps = '',
      decimalSeparator = '',
      thousandSeparator = '';
    let useDecimalSeparator = false,
      useThousandSeparator = false;
    let listOrder: string[] = [];
    const formatStr = this.formatString(params).toLowerCase();
    if (formatStr) {
      const validDateTimeSeps = formatterStrParamDef(
        'ValidDateTimeSeps',
        params,
        this.defaultParams()
      );
      const validDateSeps = formatterStrParamDef(
        'ValidDateSeps',
        params,
        this.defaultParams()
      )
        .replace('/', `\\/`)
        .replace('-', '\\-');
      const validTimeSeps = formatterStrParamDef(
        'ValidTimeSeps',
        params,
        this.defaultParams()
      );

      useDecimalSeparator = true;
      useThousandSeparator = true;
      if (validDateTimeSeps) {
        validSeps = validDateTimeSeps;
        if (validDateSeps) {
          if (validDateSeps.length === 1) {
            decimalSeparator = `\\${validDateSeps}`;
          } else if (validDateSeps.length >= 2) {
            decimalSeparator = `[${validDateSeps}]`;
          }
        }
        if (validTimeSeps) {
          if (validTimeSeps.length === 1) {
            thousandSeparator = `\\${validTimeSeps}`;
          } else if (validTimeSeps.length >= 2) {
            thousandSeparator = `[${validTimeSeps}]`;
          }
        }
      } else {
        if (validDateSeps) {
          if (validDateSeps.length === 1) {
            decimalSeparator = `\\${validDateSeps}`;
          } else {
            validSeps = validDateSeps;
            useDecimalSeparator = false;
          }
        }
        if (validTimeSeps) {
          if (validTimeSeps.length === 1) {
            thousandSeparator = `\\${validTimeSeps}`;
          } else if (validSeps.length === 0) {
            validSeps = validTimeSeps;
            useThousandSeparator = false;
          }
        }
      }

      let index = 0;
      let patternArray = [''];
      let patternIndex = 0;
      let optionalActive: boolean[] = [];
      let groupActive = false;
      while (index < formatStr.length) {
        const {
          result: ftResult,
          format: fc,
          count: fcnt,
          nextIndex,
        } = nextFormatItem(formatStr, index);
        if (ftResult) {
          index = nextIndex;
          let convertFormatItem: IConvertFormatItem;
          switch (fc) {
            case "''":
            case '"':
              break;
            case '-':
              if (!useDecimalSeparator) {
                patternArray[patternIndex] += `[${validDateSeps}]`;
              } else if (decimalSeparator) {
                patternArray[patternIndex] += decimalSeparator;
              }
              break;
            case ':':
              if (!useThousandSeparator) {
                patternArray[patternIndex] += `[${validTimeSeps}]`;
              } else if (thousandSeparator) {
                patternArray[patternIndex] += thousandSeparator;
              }
              break;
            case '.':
              patternArray[patternIndex] += `[${validDateTimeSeps}]`;
              break;
            case '(':
              patternArray[patternIndex] += '(?:';
              groupActive = true;
              break;
            case ')':
              patternArray[patternIndex] += ')';
              groupActive = false;
              break;
            case '/':
              if (optionalActive[optionalActive.length - 1] === groupActive) {
                optionalActive.splice(optionalActive.length - 1, 1);
                patternArray[patternIndex] += ')?';
              }
              if (groupActive) {
                patternArray[patternIndex] += '|';
              } else {
                patternIndex++;
                patternArray.push('');
              }
              break;
            case '|':
              if (optionalActive[optionalActive.length - 1] === groupActive) {
                optionalActive.splice(optionalActive.length - 1, 1);
                patternArray[patternIndex] += ')?';
              }
              optionalActive.push(groupActive);
              patternArray[patternIndex] += '(?:';
              break;
            case '_':
              patternArray[patternIndex] += '\\s?';
              break;
            case '~':
              patternArray[patternIndex] += '.';
              break;
            case '^':
              patternArray[patternIndex] += '\\S';
              break;
            case ' ':
              break;
            default:
              listOrder.push(fc);
              convertFormatItem = this.convertFormatItem(params, fc, fcnt);
              convertFormatItem.pattern.replace('\\\\', '\\');
              if (convertFormatItem) {
                patternArray[
                  patternIndex
                ] = `${patternArray[patternIndex]}${convertFormatItem.pattern}`;
              }
              break;
          }
        } else {
          return { pattern: null, listOrder: [] };
        }
      }
      optionalActive.forEach(() => {
        patternArray[patternIndex] += ')?';
      });
      pattern = patternArray.map(x => `(?:^${x}$)`).join('|');
    } else {
      return { pattern: null, listOrder: [] };
    }

    return { pattern: new RegExp(pattern), listOrder };
  }

  convertFormatItem(
    params: string,
    char: string,
    count: number
  ): IConvertFormatItem {
    const defaultParams = this.defaultParams();
    const assumeCentury = formatterBoolParamDef(
      'AssumeCentury',
      params,
      defaultParams
    );
    const requireCentury = formatterBoolParamDef(
      'RequireCentury',
      params,
      defaultParams
    );

    let pattern = '';
    let index = 0;
    if (char === 'd' && count > 2) {
      char = 'e';
    }

    switch (char) {
      case 'y':
        switch (count) {
          case 1:
            if (requireCentury) {
              pattern = '(\\d{4})';
            } else if (assumeCentury) {
              pattern = '((?:\\d{4})|(?:\\d{2}))';
            } else {
              pattern = '(\\d{2}|\\d{4})';
            }
            break;
          case 2:
            pattern = '(\\d{2})';
            break;
          case 4:
            pattern = '(\\d{4})';
            break;
        }
        break;
      case 'm':
        switch (count) {
          case 1:
            pattern = '(\\d{1,2})';
            break;
          case 2:
            pattern = '(\\d{2})';
            break;
          case 3:
          case 4:
            for (let i = 0; i < 12; i++) {
              pattern = `${pattern}${pattern === '' ? '' : '/'}`; //GetMonthText(i, Count = 3)
            }
            break;
        }
        break;
      case 'd':
      case 'w':
      case 'h':
      case 'n':
      case 's':
        switch (count) {
          case 1:
            pattern = '(\\d{1,2})';
            break;
          case 2:
            pattern = '(\\d{2})';
            break;
        }
        break;
      case 'e':
        switch (count) {
          case 1:
          case 2:
            pattern = '(\\d)';
            break;
          case 3:
          case 4:
            for (let i = 0; i < 7; i++) {
              pattern = `${pattern}${pattern === '' ? '' : '/'}${''}`;
            }
            break;
        }
        break;
      case 'q':
        pattern = '(\\d)';
        break;
      case 'z':
        switch (count) {
          case 1:
            pattern = '(\\d{1,3})';
            break;
          case 2:
          case 3:
            pattern = '(\\d{3})';
            break;
        }
        break;
      case 'a':
        switch (count) {
          case 1:
            break;
        }
        break;
    }

    return { pattern, index };
  }

  interpretParts(
    texts: string[],
    listOrder: string[],
    params: string
  ): { result: ParseResult; value?: string } {
    const defaultParams = this.defaultParams();
    const monthTexts = formatterStrParamDef(
      'MonthTexts',
      params,
      defaultParams
    );
    const dayTexts = formatterStrParamDef('DayTexts', params, defaultParams);
    const amStr = formatterStrParamDef('AmStr', params, this.defaultParams());
    const pmStr = formatterStrParamDef('PmStr', params, this.defaultParams());
    const centuryLimit = formatterStrParamDef(
      'CenturyLimit',
      params,
      defaultParams
    );
    const allowIncomplete = formatterBoolParamDef(
      'AllowIncomplete',
      params,
      defaultParams
    );
    const allowZero =
      allowIncomplete &&
      formatterBoolParamDef('AllowZero', params, defaultParams);
    const defMonth = formatterIntParamDef('DefMonth', params, defaultParams);
    const defDay = formatterIntParamDef('DefDay', params, defaultParams);

    const getText = (s: string): { result: boolean; text: string } => {
      const i = listOrder.indexOf(s);
      if (i < texts.length && texts[i]) {
        return { result: i >= 0, text: texts[i] };
      }
      return { result: false, text: '' };
    };
    const getNum = (
      s: string,
      date: boolean = false,
      zeroAllowed?: boolean
    ): { result: boolean; number: number } => {
      const { result: textResult, text } = getText(s);
      if (!textResult) {
        return { result: true, number: -1 };
      }

      const number = Number.parseInt(text);
      return {
        result: Boolean(
          text === '' || date
            ? number >= 0 || allowZero || zeroAllowed
            : !isNaN(number)
        ),
        number,
      };
    };
    const getMonth = (s: string): { result: boolean; month: number } => {
      const { result: textResult, text } = getText(s);
      if (!textResult) {
        return { result: true, month: -1 };
      }

      const monthResult = { result: true, month: -1 };
      if (text) {
        monthResult.month = Number.parseInt(text);
        monthResult.result = Boolean(monthResult.month >= 0 || allowZero);
        if (!monthResult.result && monthTexts) {
          monthResult.result = true;
          monthResult.month++;
        }
        if (!monthResult.result) {
          monthResult.month = 1;
          while (!monthResult.result && monthResult.month <= 12) {
            if (
              text === getMonthText(monthResult.month, false) ||
              getMonthText(monthResult.month, true)
            ) {
              monthResult.result = true;
            } else {
              monthResult.month++;
            }
          }
        }
        if (!monthResult.result) {
          monthResult.month = -1;
        }
      }
      return monthResult;
    };
    const getWeekDay = (s: string): { result: boolean; weekDay: number } => {
      const { result: textResult, text } = getText(s);
      if (!textResult) {
        return { result: true, weekDay: -1 };
      }

      const weekResult = { result: true, weekDay: -1 };
      if (text) {
        weekResult.weekDay = Number.parseInt(s);
        weekResult.result = Boolean(weekResult.weekDay);
        if (!weekResult.result && dayTexts) {
          weekResult.result = true;
          weekResult.weekDay++;
        }
        if (!weekResult.weekDay) {
          weekResult.weekDay = 1;
          while (!weekResult.result && weekResult.weekDay <= 7) {
            if (
              text === getWeekDayText(weekResult.weekDay, false) ||
              text === getWeekDayText(weekResult.weekDay, true)
            ) {
              weekResult.result = true;
            } else {
              weekResult.weekDay++;
            }
          }
        }
        if (!weekResult.weekDay) {
          weekResult.weekDay = -1;
        }
      }

      return weekResult;
    };

    const getList = (
      s: string,
      list: string[]
    ): { result: boolean; list: number } => {
      const { result: textResult, text } = getText(s);
      if (!textResult) {
        return { result: true, list: -1 };
      }

      const listResult = { result: true, list: -1 };
      if (text) {
        const index = list.indexOf(text);
        listResult.list = index ? index : -1;
        listResult.result = listResult.list > 0;
      }

      return listResult;
    };

    const result: { result: ParseResult; value?: string } = {
      result: ParseResult.ERROR,
    };
    let tempStr = { result: false, text: '' };
    let tempInt = -1;
    let cl = -1;
    const y = getNum('y', true, true);
    const m = getMonth('m');
    const d = getNum('d', true);
    const x = getNum('x');
    const o = getNum('o');
    const w = getNum('w');
    const e = getWeekDay('e');
    const q = getNum('q');
    const h = getNum('h');
    const n = getNum('n');
    const s = getNum('s');
    const z = getNum('z');
    const t = getNum('t');
    const a = getList('a', [amStr, pmStr]);
    if (
      y.result &&
      m.result &&
      d.result &&
      x.result &&
      o.result &&
      w.result &&
      e.result &&
      q.result &&
      h.result &&
      n.result &&
      s.result &&
      z.result &&
      t.result &&
      a.result
    ) {
      if (y.number >= 0 || o.number >= 0) {
        let needCentury = false;
        tempStr = getText('y');
        if (y.number >= 0 && tempStr.result && tempStr.text.length !== 4) {
          needCentury = true;
        }
        if (o.number >= 0 && y.number < 0) {
          y.number = o.number;
          tempStr = getText('o');
          if (tempStr.result && tempStr.text.length !== 4) {
            needCentury = true;
          }
        }
        if (needCentury) {
          cl = new Date().getFullYear() + 20;
          if (centuryLimit) {
            tempInt = Number.parseInt(tempStr.text);
            const centuryNumber = Number.parseInt(centuryLimit);
            if (['+', '-'].indexOf(centuryLimit[0]) !== -1 && tempInt) {
              cl = new Date().getFullYear() + centuryNumber;
            } else if (tempInt) {
              if (centuryNumber >= 1000) {
                cl = centuryNumber;
              } else {
                cl = new Date().getFullYear() + centuryNumber;
              }
            }
          }
          y.number = cl - (cl % 100) + y.number;
          if (y.number > cl) {
            y.number -= 100;
          }
        }
        if (q.number >= 0 && q.number <= 4 && m.month < 0 && d.number < 0) {
          if (q.number === 0 && allowIncomplete) {
            q.number = 1;
          }
          m.month = getQuarterMonth(m.month);
          d.number = 1;
        }
        if (
          y.number >= 1000 &&
          y.number <= 9900 &&
          d.number < 0 &&
          x.number >= 0
        ) {
          if (x.number === 0 && allowIncomplete) {
            x.number = 1;
          }
          if (x.number < 1 || x.number > 366) {
            // EXIT
            return { result: ParseResult.ERROR };
          }
          const date = moment(weekNoToDate(y.number, w.number, e.weekDay - 1));
          tempInt = date.year();
          m.month = date.month() + 1;
          d.number = date.date();
          if (tempInt !== y.number) {
            // EXIT
            return { result: ParseResult.ERROR };
          }
        }
        if (
          y.number >= 1000 &&
          y.number <= 9900 &&
          m.month < 0 &&
          d.number < 0 &&
          w.number >= 0
        ) {
          if (e.weekDay < 0) {
            e.weekDay = 1;
          }
          if (w.number === 0 && allowIncomplete) {
            w.number = 1;
          }
          if (w.number < 1 || w.number > 53 || e.weekDay < 1 || e.weekDay > 7) {
            // EXIT
            return { result: ParseResult.ERROR };
          }
          const dateValue = moment(
            weekNoToDate(y.number, w.number, e.weekDay - 1)
          );
          if (w.number === 53 && dateValue.week() !== w.number) {
            // EXIT
            return { result: ParseResult.ERROR };
          }
          y.number = dateValue.year();
          m.month = dateValue.month() + 1;
          d.number = dateValue.date();
        }
        if (allowIncomplete) {
          if (m.month < 0 && d.number < 0) {
            m.month = defMonth;
          }
          if (d.number < 0) {
            d.number = defDay;
            if (d.number > 28 && m.month >= 1 && m.month <= 12) {
              d.number = Math.min(
                d.number,
                moment(`${y.number}-${m.month}`).daysInMonth()
              );
            }
          }
        }
      }
      if (h.number < 0 && t.number >= 0 && a.list >= 0) {
        if (a.list > 0) {
          if (t.number === 12) {
            h.number = 0;
          } else {
            h.number = t.number + 12;
          }
        } else {
          h.number = t.number;
        }
      }
      if (h.number >= 0) {
        if (n.number < 0 && s.number < 0 && z.number < 0) {
          n.number = 0;
        }
        if (s.number < 0 && z.number < 0) {
          s.number = 0;
        }
        if (z.number < 0) {
          z.number = 0;
        }
      }

      switch (this.mode()) {
        case TEDParseDateTimeMode.pdmYM:
          d.number = 1;
          break;
        case TEDParseDateTimeMode.pdmY:
          m.month = 1;
          d.number = 1;
          break;
        case TEDParseDateTimeMode.pdmHMS:
          z.number = 0;
          break;
        case TEDParseDateTimeMode.pdmHM:
          s.number = 0;
          z.number = 0;
          break;
        case TEDParseDateTimeMode.pdmH:
          n.number = 0;
          s.number = 0;
          z.number = 0;
          break;
      }

      const stringMode =
        this.mode() === TEDParseDateTimeMode.pdmDateString ||
        this.mode() === TEDParseDateTimeMode.pdmDateTimeString;

      let dateResult = this.makeDateTime(
        {
          ty: y.number,
          tm: m.month,
          td: d.number,
          th: h.number,
          tn: n.number,
          ts: s.number,
          tms: z.number,
        },
        stringMode
      );

      let value: string | undefined;
      if (
        y.number !== -1 &&
        m.month !== -1 &&
        d.number !== -1 &&
        h.number !== -1 &&
        n.number !== -1 &&
        s.number !== -1
      ) {
        const dateString = dateResult.value?.padStart(19, '0');
        value = stringMode
          ? dateString
              ?.replaceAll('-', '')
              .replaceAll(':', '')
              ?.replaceAll(' ', '')
          : dateString;
      } else if (y.number !== -1 && m.month !== -1 && d.number !== -1) {
        const dateString = dateResult.value?.padStart(10, '0');
        value = stringMode
          ? dateString
              ?.replaceAll('-', '')
              .replaceAll(':', '')
              ?.replaceAll(' ', '')
          : dateString;
      } else if (h.number !== -1 && n.number !== -1 && s.number !== -1) {
        const dateString = dateResult.value ?? '';
        value = stringMode
          ? dateString
              ?.replaceAll('-', '')
              .replaceAll(':', '')
              ?.replaceAll(' ', '')
          : dateString;
      }

      result.result = dateResult.result;
      result.value = value;
      if (result.result > ParseResult.OK) {
        result.value = undefined;
      }
    }

    return result;
  }

  makeDateTime(
    dateParts: IInternalDateFormat,
    stringMode: Boolean
  ): {
    result: ParseResult;
    value: string | null;
  } {
    const dateResult: { result: ParseResult; value: any } = {
      result: ParseResult.OK,
      value: null,
    };
    if (dateParts.ty >= 0 || dateParts.tm >= 0 || dateParts.td >= 0) {
      const date = encodeDate(dateParts);
      if (dateParts.ty >= 1 && dateParts.tm >= 1 && dateParts.td >= 1 && date) {
        if (dateParts.th >= 0 && dateParts.tn >= 0 && dateParts.ts >= 0) {
          dateResult.value = date.toLocaleString('sv-SE');
        } else {
          dateResult.value = date.toLocaleDateString('sv-SE');
        }
      } else if (
        stringMode &&
        dateParts.ty >= 1 &&
        dateParts.tm >= 0 &&
        dateParts.tm <= 12 &&
        dateParts.td === 0
      ) {
        dateResult.value =
          dateParts.ty.toString().padStart(4, '0') +
          '-' +
          dateParts.tm.toString().padStart(2, '0') +
          '-' +
          dateParts.td.toString().padStart(2, '0');
      } else {
        dateResult.result = ParseResult.ERROR;
      }
    } else if (dateParts.th >= 0) {
      const time = encodeTime(dateParts);
      // ↓↓ to allow bookings at 00:00:00 ↓↓
      if (dateParts.th >= 0 && time) {
        dateResult.value = time.toLocaleTimeString('sv-SE');
      } else {
        dateResult.result = ParseResult.ERROR;
      }
    }

    return dateResult;
  }

  matchPattern(
    s: string,
    pattern: string
  ): { result: ParseResult; value: any } {
    const result: { result: ParseResult; value: any } = {
      result: ParseResult.OK,
      value: null,
    };

    return result;
  }
}
